import React from "react";
import { Link } from "react-router-dom";
import { ArrowUpRight, MapPin } from "lucide-react";
import Seo from "@/components/Seo";
import { CheckRow, CtaBand, FaqBlock, Label, PageHero, SectionHead } from "@/components/Shared";
import { Reveal } from "@/components/Reveal";
import { BremenSkyline, Stadtmusikanten } from "@/components/graphics/Marks";
import { BRAND, DISTRICTS, IMG } from "@/lib/site";

const crumbs = [
  { label: "Startseite", to: "/" },
  { label: "Einsatzgebiet", to: "/einsatzgebiet" },
];

const faq = [
  { q: "In welchem Gebiet bieten Sie Winterdienst an?", a: "Im gesamten Bremer Stadtgebiet mit allen Stadtteilen sowie im direkt angrenzenden Umland. Für Objekte weiter außerhalb geben wir eine individuelle Einschätzung." },
  { q: "Bedienen Sie auch Bremen-Nord?", a: "Ja. Burglesum, Vegesack und Blumenthal gehören zum Einsatzgebiet." },
  { q: "Warum nennen Sie keine langen Ortslisten?", a: "Weil Winterdienst Logistik ist. Wir nennen nur Gebiete, die wir mit verlässlichen Zeitfenstern tatsächlich bedienen können." },
  { q: "Können mehrere Nachbarn gemeinsam anfragen?", a: "Ja, das ist planerisch sinnvoll. Objekte in einer Nachbarschaft lassen sich in einer Tour bündeln." },
];

const Einsatzgebiet = () => (
  <main data-testid="page-einsatzgebiet">
    <Seo
      title="Einsatzgebiet Winterdienst Bremen | Alle Stadtteile"
      description="Einsatzgebiet von Bremer Schneemann: Winterdienst im gesamten Bremer Stadtgebiet – von Mitte und Findorff über Hemelingen und Vahr bis Vegesack und Blumenthal."
      path="/einsatzgebiet"
      breadcrumbs={crumbs}
      faq={faq}
      service="Winterdienst Einsatzgebiet Bremen"
      image={IMG.bremenMarket}
    />
    <PageHero
      label="/ Einsatzgebiet"
      titleLines={["Winterdienst in", "Bremen – und im", "direkten Umland."]}
      intro="Bremer Schneemann ist Ihr Ansprechpartner für Winterdienst in Bremen und Umgebung. Unser Kerngebiet ist das Stadtgebiet mit allen Stadtteilen."
      image={IMG.bremenMarket}
      breadcrumbs={crumbs}
    />


    <section className="bg-white py-16 lg:py-24">
      <div className="shell grid gap-14 lg:grid-cols-[1.1fr_0.9fr] lg:gap-20">
        <div>
          <SectionHead
            label="/ Kerngebiet Bremen"
            title="Dichte Touren statt langer Anfahrten."
            text="Winterdienst funktioniert nur über planbare Touren. Je dichter die Objekte beieinander liegen, desto verlässlicher sind die Zeitfenster – besonders bei Dauerschneefall, wenn nachgeräumt werden muss."
          />
          <div className="prose-de mt-7">
            <p className="text-[15px] sm:text-base">
              Unser Standort liegt an der Flughafenallee 26 in 28199 Bremen. Von
              dort erreichen wir den Osten Bremens – Hemelingen, Sebaldsbrück, Hastedt, Vahr
              und Osterholz – besonders schnell. Über die Ringstraßen sind aber auch Mitte,
              Neustadt, Findorff, Walle, Gröpelingen, Schwachhausen, Horn-Lehe, Oberneuland,
              Obervieland, Huchting und Bremen-Nord gut angebunden.
            </p>
            <p className="text-[15px] sm:text-base">
              Wir nennen bewusst keine erfundenen Ortslisten. Liegt Ihr Objekt im direkt
              angrenzenden Umland, fragen Sie einfach an – wir sagen Ihnen offen, ob es sich
              in unsere Tourenplanung einfügen lässt.
            </p>
          </div>

          <Reveal delay={0.1}>
            <div className="mt-9">
              <Label>/ Stadtteile im Einsatzgebiet</Label>
              <div className="mt-5 flex flex-wrap gap-2" data-testid="districts-list">
                {DISTRICTS.map((d) => (
                  <span
                    key={d}
                    className="rounded-full border border-[#DCE5EF] bg-[#F5F8FC] px-3.5 py-1.5 text-[12.5px] font-medium text-[#10253A]"
                  >
                    {d}
                  </span>
                ))}
                <span className="rounded-full bg-[#FF6B00] px-3.5 py-1.5 text-[12.5px] font-semibold text-white">
                  + direktes Umland auf Anfrage
                </span>
              </div>
            </div>
          </Reveal>

          <Reveal delay={0.16}>
            <div className="mt-10 overflow-hidden rounded-2xl">
              <img
                src={IMG.bremenFacade}
                alt="Historische Gebäude in der Bremer Altstadt"
                loading="lazy"
                className="h-64 w-full object-cover"
              />
            </div>
          </Reveal>
        </div>

        <aside className="lg:sticky lg:top-28 lg:self-start">
          <Reveal>
            <div className="relative overflow-hidden rounded-[22px] bg-[#EEF6FF] p-7">
              <Label>/ Bremen</Label>
              <p className="mt-3 text-[19px] font-bold leading-snug text-[#062B52]">
                Stadtgebiet Bremen im Überblick
              </p>
              <BremenSkyline className="mt-8 h-auto w-full opacity-90" color="#062B52" />
              <Stadtmusikanten className="absolute right-5 top-5 h-28 opacity-25" color="#0757B8" />
            </div>
          </Reveal>
          <Reveal delay={0.1}>
            <div className="mt-5 rounded-2xl border border-[#DCE5EF] bg-white p-6">
              <Label>/ Standort</Label>
              <p className="mt-4 flex items-start gap-3 text-[14.5px] leading-relaxed text-[#10253A]">
                <MapPin className="mt-0.5 h-4 w-4 shrink-0 text-[#FF6B00]" />
                <span>
                  {BRAND.name}
                  <br />
                  {BRAND.street}
                  <br />
                  {BRAND.streetExtra}
                  <br />
                  {BRAND.zip} {BRAND.city}
                  <br />
                  <span className="mt-2 block text-[13px] text-[#66788A]">{BRAND.fleet}</span>
                </span>
              </p>
              <ul className="mt-6 space-y-3 border-t border-[#DCE5EF] pt-6">
                <CheckRow>Winterdienst im gesamten Stadtgebiet</CheckRow>
                <CheckRow>Private und gewerbliche Objekte</CheckRow>
                <CheckRow>Umland auf Anfrage und nach Machbarkeit</CheckRow>
              </ul>
              <Link to="/kontakt" data-testid="area-page-cta" className="btn btn-orange mt-6 h-12 w-full text-sm">
                Winterdienst anfragen <ArrowUpRight className="h-4 w-4" />
              </Link>
            </div>
          </Reveal>
          <Reveal delay={0.16}>
            <div className="mt-5 rounded-2xl bg-[#062B52] p-6 text-white">
              <Label tone="orange">/ Weiterlesen</Label>
              <div className="mt-4 grid gap-2">
                {[
                  { t: "Winterdienst Bremen & Umgebung", to: "/winterdienst-bremen-umgebung" },
                  { t: "Winterdienst Bremen", to: "/winterdienst-bremen" },
                  { t: "Winterdienst Privat", to: "/winterdienst-bremen-privat" },
                  { t: "Winterdienst Gewerbe", to: "/winterdienst-bremen-gewerbe-hausverwaltung" },
                ].map((l) => (
                  <Link
                    key={l.to}
                    to={l.to}
                    data-testid={`area-link${l.to.replace(/\//g, "-")}`}
                    className="flex items-center justify-between gap-3 rounded-xl bg-white/[0.07] px-4 py-3 text-[13.5px] font-medium transition-colors hover:bg-white/[0.14]"
                  >
                    {l.t}
                    <ArrowUpRight className="h-3.5 w-3.5 text-[#FF6B00]" />
                  </Link>
                ))}
              </div>
            </div>
          </Reveal>
        </aside>
      </div>
    </section>

    <FaqBlock items={faq} title="Fragen zum Einsatzgebiet" />
    <CtaBand />
  </main>
);

export default Einsatzgebiet;
